import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  setDoc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth, messaging } from "../firebaseConfig";
import {
  GoogleMap,
  Marker,
  Circle,
  InfoWindow,
  Polyline,
  useLoadScript,
} from "@react-google-maps/api";
import { useNavigate  } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getToken } from "firebase/messaging";
import { VisitList } from "../clock/JobCheckIn";
import { JOB_VISITS, FAR_FROM_JOB_METERS, formatMinutes, visitMinutes } from "../clock/jobVisits";

const mapContainerStyle = { width: "100%", height: "400px" };
const defaultCenter = { lat: 38.8048, lng: -77.0469 };

// Filters
const LOCATION_OPTIONS = [
  "All Locations",
  "Sterling VA",
  "Martinsburg WV",
  "Frederick MD",
  "Tampa FL",
  "Suffolk NY",
];

const ROLE_OPTIONS = [
  "All Roles",
  "Admin",
  "Manager",
  "Assistant",
  "Subcontractor",
  "Tech",
];

const DATE_FILTER_OPTIONS = [
  "TODAY",
  "YESTERDAY",
  "LAST_WEEK",
  "THIS_MONTH",
  "CUSTOM",
];

// --------------------
// Map layers (what is drawn on the map). Saved per browser so a supervisor's
// choice sticks. Defaults keep the map readable: workers + today's jobs.
// --------------------
const MAP_PREFS_KEY = "supervisor_map_layers_v1";
const DEFAULT_MAP_LAYERS = {
  clockInPins: false, // arrows where each worker clocked in
  jobSites: true, // geofence circles
  jobs: true, // scheduled job pins
  jobsRange: "today", // "today" | "filter" (follow the Date Range filter)
  labels: false, // names printed next to job / site pins
};
const loadMapLayers = () => {
  try {
    const raw = localStorage.getItem(MAP_PREFS_KEY);
    return raw ? { ...DEFAULT_MAP_LAYERS, ...JSON.parse(raw) } : { ...DEFAULT_MAP_LAYERS };
  } catch {
    return { ...DEFAULT_MAP_LAYERS };
  }
};
const HOUSE_PATH = "M12 3L2 12h3v8h6v-5h2v5h6v-8h3z";
// Jobs closer together than ~10 m (same building) share one pin.
const coordKey = (lat, lng) => `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;

// --------------------
// Helper functions
// --------------------

// basic sanity check for lat/lng objects used by logs/sites
const isValidLatLng = (loc) => {
  if (!loc) return false;
  const lat = Number(loc.latitude ?? loc.lat);
  const lng = Number(loc.longitude ?? loc.lng);
  return !isNaN(lat) && !isNaN(lng);
};

// Haversine distance in meters
const distanceInMeters = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const metersToMiles = (m) => m / 1609.34;

const formatMiles = (miles) => {
  if (!miles && miles !== 0) return "";
  if (miles < 1) return miles.toFixed(2) + " mi";
  return miles.toFixed(1) + " mi";
};

const getEtaMinutesFromMiles = (miles) => {
  if (!miles && miles !== 0) return null;
  const etaHours = miles / 3; // 3 mph walking avg
  return Math.ceil(etaHours * 60);
};

const getWorkerGeofenceStatus = (loc, assignedSite) => {
  if (!loc || !assignedSite) return null;

  const dist = distanceInMeters(
    Number(loc.latitude),
    Number(loc.longitude),
    Number(assignedSite.latitude),
    Number(assignedSite.longitude)
  );

  const inside = dist <= Number(assignedSite.radiusMeters);

  return {
    inside,
    dist: Math.round(dist),
    etaMinutes: Math.ceil((dist / 1.4) / 60), // 1.4 m/s walking
  };
};

const getSpeedMphFromRoute = (route = []) => {
  if (!Array.isArray(route) || route.length < 2) return 0;

  const last = route[route.length - 1];
  const prev = route[route.length - 2];

  if (!isValidLatLng(last) || !isValidLatLng(prev)) return 0;

  const distMeters = distanceInMeters(
    last.latitude,
    last.longitude,
    prev.latitude,
    prev.longitude
  );

  const timeSec = (last.timestamp - prev.timestamp) / 1000;
  if (timeSec <= 0) return 0;

  const metersPerSec = distMeters / timeSec;
  return metersPerSec * 2.23694; // mph
};

// Any Firestore Timestamp / Date / millis / string -> Date (or null)
const toDateSafe = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const timeAgo = (date) => {
  if (!date) return "N/A";
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
};

// A clocked-in phone sends every ~60s while the app is open. Older than this
// means the app is closed, the phone is asleep, or location is off.
const STALE_MINUTES = 10;

const getPingState = (log) => {
  const requested = toDateSafe(log?.pingRequestedAt);
  if (!requested) return null;
  const answeredFor = log?.pingAnsweredFor;
  const answered = answeredFor && answeredFor === requested.getTime();
  if (answered && log.pingError) {
    return { text: "Phone answered: location off", color: "#b45309" };
  }
  if (answered) {
    return {
      text: `Answered ${timeAgo(toDateSafe(log.pingRespondedAt))}`,
      color: "#0F9D58",
    };
  }
  return { text: `Waiting… (sent ${timeAgo(requested)})`, color: "#6b7280" };
};

const mapsLink = (loc) =>
  loc && isValidLatLng(loc)
    ? `https://www.google.com/maps?q=${Number(loc.latitude ?? loc.lat)},${Number(loc.longitude ?? loc.lng)}`
    : null;

const getWorkerStatus = (geofenceInside, speedMph, hasSite = true) => {
  if (geofenceInside) return { text: "ON SITE", color: "#00C851" }; // GREEN
  if (speedMph > 1) return { text: "MOVING", color: "#4285F4" }; // BLUE
  if (speedMph <= 1 && speedMph > 0) return { text: "IDLE", color: "#F4B400" }; // YELLOW
  if (!hasSite) return { text: "CLOCKED IN", color: "#2563eb" }; // no site assigned
  return { text: "OUT OF SITE", color: "#ff4444" }; // RED
};

// simple worker color pins
const getUserColorPin = (userId) => {
  const colors = ["red", "blue", "green", "purple", "orange", "yellow", "pink"];
  let idx = 0;
  for (let i = 0; i < userId.length; i++) idx += userId.charCodeAt(i);
  const color = colors[idx % colors.length];
  return `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`;
};

const getUserColorHex = (userId) => {
  const palette = [
    "#DB4437",
    "#4285F4",
    "#0F9D58",
    "#A142F4",
    "#F4B400",
    "#FF7043",
    "#34A853",
  ];
  let idx = 0;
  for (let i = 0; i < userId.length; i++) idx += userId.charCodeAt(i);
  return palette[idx % palette.length];
};

// date range helper
const getDateRange = (filter, customStart, customEnd) => {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

  let start = null;
  let end = null;

  switch (filter) {
    case "TODAY": {
      start = startOfDay(now);
      end = endOfDay(now);
      break;
    }
    case "YESTERDAY": {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      start = startOfDay(y);
      end = endOfDay(y);
      break;
    }
    case "LAST_WEEK": {
      const s = new Date(now);
      s.setDate(now.getDate() - 7);
      start = startOfDay(s);
      end = endOfDay(now);
      break;
    }
    case "THIS_MONTH": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      start = s;
      end = e;
      break;
    }
    case "CUSTOM": {
      if (customStart) {
        start = new Date(customStart + "T00:00:00");
      }
      if (customEnd) {
        end = new Date(customEnd + "T23:59:59");
      }
      break;
    }
    default:
      break;
  }

  return { start, end };
};

// --------------------------
// Step-by-step "How To" guide shown under All Workers (Clocked-In First).
// Each guide is a list of numbered steps. Steps match the exact button and
// field names on this page so a supervisor can follow along on screen.
// --------------------------
const HOW_TO_GUIDES = [
  {
    key: "setFencing",
    label: "Set Fencing",
    intro:
      "Lock a worker so they can only clock in when they are inside a job site's circle.",
    steps: [
      "Make sure the job site already exists. Check the \"Existing Job Sites\" list at the bottom of this page. If it is not there, follow \"Add New Job Site\" first.",
      "Scroll to the top of the page and click the \"Set Fencing\" button (under \"Recenter Map\"). A panel slides open on the right side.",
      "Find the worker. You can type their name in \"Search workers…\" to narrow the list.",
      "Click the worker's name. It highlights blue and their settings appear at the bottom of the panel.",
      "Under \"Assigned Job Site\", pick the job site from the dropdown.",
      "Check the box \"Require Geofence for Clock-In\". The note under the dropdown should now say \"Clock-in is blocked outside this site.\"",
      "Changes save automatically. The worker's line in the panel should say Geofence: On and show the site name.",
      "Click \"Close\". The worker's Job Site column in the table below now shows the site you picked.",
    ],
  },
  {
    key: "removeFencing",
    label: "Remove Fencing",
    intro: "Let a worker clock in from anywhere again.",
    steps: [
      "Scroll to the top of the page and click \"Set Fencing\". The panel opens on the right.",
      "Find the worker (use \"Search workers…\" if needed) and click their name.",
      "Click the red \"Remove Fencing (clock in anywhere)\" button at the bottom of the panel. This turns fencing off AND clears their job site in one click.",
      "The worker's line should now say Geofence: Off and Site: None.",
      "Click \"Close\". The worker's Job Site column in the table below now shows \"No Assigned Site\".",
      "Tip: If you only want to stop blocking clock-in but still see their distance from the site, just uncheck \"Require Geofence for Clock-In\" and leave the site picked instead.",
    ],
  },
  {
    key: "addSite",
    label: "Add New Job Site",
    intro: "Create a job site circle on the map that workers can be fenced to.",
    steps: [
      "Scroll to the top of the page and click \"Add New Job Site\". A small \"New Job Site\" box appears and the button changes to \"Cancel New Job Site\".",
      "Type the name in \"Job Site Name\" (example: 8610 Main St).",
      "Set \"Radius (meters)\". This is how big the circle is. 150 meters (about 500 ft) is the default and works for most sites. Use a bigger number for large properties.",
      "Place the site: type the address in \"Address (optional)\" and click \"Find\". OR scroll down to the map, zoom in on the property, and click once on the center of the site.",
      "Check the New Job Site box. It should now show \"Location:\" with numbers. If it doesn't, try Find again or click the map.",
      "Click \"Save Job Site\". The box closes, a blue house with a circle appears on the map, and the site is added to \"Existing Job Sites\" at the bottom.",
      "Need to adjust the circle later? Change the radius number next to the site in \"Existing Job Sites\" and click away to save.",
      "To put workers on this site, follow \"Set Fencing\".",
    ],
  },
  {
    key: "removeSite",
    label: "Remove Job Site",
    intro: "Delete a job site you no longer need.",
    steps: [
      "Scroll to \"Existing Job Sites\" at the bottom of this page. Each site shows how many workers are on it.",
      "Optional: if you want those workers on a different site, move them first with \"Set Fencing\" so they never lose their fence.",
      "Click \"Delete\" next to the job site name.",
      "Read the \"Are you sure?\" popup. It lists every worker still assigned to the site.",
      "Click OK to delete, or Cancel to stop. If you click OK, any workers still on the site have their fencing removed and can clock in anywhere.",
      "The site and its circle are removed right away. There is no undo. If you deleted the wrong one, add it again with \"Add New Job Site\" and re-fence the workers.",
    ],
  },
];

const SupervisorHowTo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(HOW_TO_GUIDES[0].key);
  const guide =
    HOW_TO_GUIDES.find((g) => g.key === activeKey) || HOW_TO_GUIDES[0];

  return (
    <div
      style={{
        border: "1px solid #cfd8e3",
        borderRadius: 6,
        background: "#f7f9fc",
        marginTop: 6,
        marginBottom: 10,
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          textAlign: "left",
          background: "transparent",
          border: "none",
          padding: "8px 12px",
          fontWeight: 600,
          fontSize: 14,
          cursor: "pointer",
        }}
      >
        {isOpen ? "▾" : "▸"} How To: Set Fencing, Remove Fencing, Add / Remove
        Job Sites
      </button>

      {isOpen && (
        <div style={{ padding: "0 12px 12px" }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 10,
            }}
          >
            {HOW_TO_GUIDES.map((g) => {
              const isActive = g.key === activeKey;
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setActiveKey(g.key)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 16,
                    border: isActive ? "1px solid #4285F4" : "1px solid #ccc",
                    background: isActive ? "#4285F4" : "#fff",
                    color: isActive ? "#fff" : "#333",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {g.label}
                </button>
              );
            })}
          </div>

          <div style={{ fontSize: 13, color: "#444", marginBottom: 8 }}>
            {guide.intro}
          </div>

          <ol style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
            {guide.steps.map((step, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  marginBottom: 8,
                  fontSize: 14,
                  lineHeight: 1.4,
                }}
              >
                <span
                  style={{
                    flex: "0 0 24px",
                    height: 24,
                    borderRadius: "50%",
                    background: "#4285F4",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

const SupervisorDashboard = () => {
    // ✅ ADD THIS (route guard)
    const navigate = useNavigate();
    const [userRole, setUserRole] = useState("");
  
      // ✅ ADD THIS (role-based access control)
  useEffect(() => {
    const run = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        navigate("/");
        return;
      }

      const usersRef = collection(db, "users");
      const snap = await getDocs(usersRef);
      const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const me = users.find((u) => u.id === currentUser.uid);

      const role = me?.role || "";
      setUserRole(role);

      const allowed = ["Admin", "Manager"].includes(role);
      if (!allowed) {
        window.alert(
          "🚫 Access denied: You don’t have permission to view the Admin Portal."
        );
        navigate("/");
      }
    };

    run();
  }, [navigate]);

  // live logs & last logs
  const [activeLogs, setActiveLogs] = useState([]);
  const [lastLogByUser, setLastLogByUser] = useState({}); // userId -> last log

  // core collections
  const [users, setUsers] = useState([]);
  const [jobSites, setJobSites] = useState([]);

  // filters
  const [selectedUserIds, setSelectedUserIds] = useState([]); // map filter
  const [locationFilter, setLocationFilter] = useState("All Locations");
  const [roleFilter, setRoleFilter] = useState("All Roles");
  const [dateFilter, setDateFilter] = useState("THIS_MONTH");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [activeJobPopup, setActiveJobPopup] = useState(null);

  const handleJobMarkerClick = (job) => {
    setActiveJobPopup(job);
  };
  
  // Worker locations are the point of this page, so they show by default.
  const [showWorkersOnMap, setShowWorkersOnMap] = useState(true);
  const [activeWorkerPopup, setActiveWorkerPopup] = useState(null); // log id
  const [showClockInPins, setShowClockInPins] = useState(() => loadMapLayers().clockInPins);
  const [mapLayers, setMapLayers] = useState(loadMapLayers);
  const setLayer = (key, value) => setMapLayers((p) => ({ ...p, [key]: value }));
  const [activeSitePopup, setActiveSitePopup] = useState(null);
  // Job check-ins (job_visits)
  const [openVisits, setOpenVisits] = useState([]); // everyone checked in right now
  const [rangeVisits, setRangeVisits] = useState([]); // check-ins in the Date Range
  const [showVisitLog, setShowVisitLog] = useState(false);
  const [pingingIds, setPingingIds] = useState({});
  const [fenceSearch, setFenceSearch] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [didFitWorkers, setDidFitWorkers] = useState(false);
  const [, setNowTick] = useState(0);

  // map state
  const [mapRef, setMapRef] = useState(null);

  // job site creation
  const [addingSiteMode, setAddingSiteMode] = useState(false);
  const [newSiteLocation, setNewSiteLocation] = useState(null);
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteRadius, setNewSiteRadius] = useState(150);

  // fencing panel
  const [isFencePanelOpen, setIsFencePanelOpen] = useState(false);
  const [fenceUserId, setFenceUserId] = useState(null);
  const [fenceUser, setFenceUser] = useState(null);
  const [fenceAssignedSiteId, setFenceAssignedSiteId] = useState("");

  // FCM notifications
  const [tokenError, setTokenError] = useState(null);

  // jobs for a selected user
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [selectedUserForJobs, setSelectedUserForJobs] = useState(null);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // job markers (geocoded)
  const [jobMarkers, setJobMarkers] = useState([]);
  const [geocodeCache, setGeocodeCache] = useState({});

  const [jobTotals, setJobTotals] = useState({});


  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  // --------------------------
  // Load active clocked-in logs
  // --------------------------
  useEffect(() => {
    // Only listen when the toggle is ON
    if (!showWorkersOnMap) {
      setActiveLogs([]);
      return;
    }
  
    const qActive = query(
      collection(db, "time_logs"),
      where("clockOut", "==", null)
    );
  
    const unsub = onSnapshot(qActive, (snap) => {
      const logs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setActiveLogs(logs);
    });
  
    return () => unsub();
  }, [showWorkersOnMap]);
  

  useEffect(() => {
    fetchJobsForMap();
  }, [
    dateFilter,
    customStartDate,
    customEndDate,
    selectedUserIds,
    users
  ]);
  
  // Re-render every 30s so "x min ago" / staleness stays current.
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // --------------------------
  // Load last log per user (for logged out)
  // --------------------------
  useEffect(() => {
    const loadLastLogs = async () => {
      const snap = await getDocs(collection(db, "time_logs"));
      const byUser = {};

      snap.docs.forEach((d) => {
        const data = d.data();
        const userId = data.userId;
        if (!userId) return;

        const clockOut = data.clockOut?.toDate?.();
        const clockIn = data.clockIn?.toDate?.();
        const logTime = clockOut || clockIn;
        if (!logTime) return;

        const existing = byUser[userId];
        if (!existing || logTime > existing.time) {
          byUser[userId] = { log: { id: d.id, ...data }, time: logTime };
        }
      });

      const map = {};
      Object.keys(byUser).forEach((uid) => {
        map[uid] = byUser[uid].log;
      });
      setLastLogByUser(map);
    };

    loadLastLogs();
  }, []);

  // --------------------------
  // Load all users
  // --------------------------
  useEffect(() => {
    const loadUsers = async () => {
      const snap = await getDocs(collection(db, "users"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsers(list);
    };
    loadUsers();
  }, []);

  // --------------------------
  // Load job sites
  // --------------------------
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "job_sites"), (snap) => {
      const sites = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setJobSites(sites);
    });
    return () => unsub();
  }, []);

  // --------------------------
  // Fencing selection changes
  // --------------------------
  useEffect(() => {
    if (!fenceUserId) {
      setFenceUser(null);
      setFenceAssignedSiteId("");
      return;
    }
    const u = users.find((user) => user.id === fenceUserId) || null;
    setFenceUser(u);
    setFenceAssignedSiteId(u?.assignedJobSite || "");
  }, [fenceUserId, users]);

  // Save map layer choices for next time.
  useEffect(() => {
    try {
      localStorage.setItem(
        MAP_PREFS_KEY,
        JSON.stringify({ ...mapLayers, clockInPins: showClockInPins })
      );
    } catch {}
  }, [mapLayers, showClockInPins]);

  // --------------------------
  // Job check-ins: who is at a job right now
  // --------------------------
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, JOB_VISITS), where("status", "==", "checked_in")),
      (snap) => setOpenVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("Open job check-ins listen failed:", err)
    );
    return () => unsub();
  }, []);

  // Job check-ins inside the Date Range filter (log + per-job times).
  // Single-field range on checkInAt: no composite index needed.
  useEffect(() => {
    const { start, end } = getDateRange(dateFilter, customStartDate, customEndDate);
    const parts = [];
    if (start) parts.push(where("checkInAt", ">=", start));
    if (end) parts.push(where("checkInAt", "<=", end));
    const q = parts.length
      ? query(collection(db, JOB_VISITS), ...parts)
      : collection(db, JOB_VISITS);
    const unsub = onSnapshot(
      q,
      (snap) => setRangeVisits(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.warn("Job check-in log listen failed:", err)
    );
    return () => unsub();
  }, [dateFilter, customStartDate, customEndDate]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "job_totals"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.data().jobId] = d.data();
      });
      setJobTotals(map);
    });
    return () => unsub();
  }, []);
  
  // --------------------------
  // Register service worker + FCM token
  // --------------------------
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then(() => console.log("Service worker registered"))
        .catch((err) => console.error("SW registration failed", err));
    }

    const requestNotificationPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        // Priority's own Web Push key (Firebase Console → Project settings →
        // Cloud Messaging → Web Push certificates), set in the .env file.
        // The old hard-coded key belonged to a different Firebase project,
        // which is why FCM answered 401.
        const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        if (!vapidKey) {
          console.warn("Push notifications are off: REACT_APP_FIREBASE_VAPID_KEY is not set in .env");
          return;
        }

        const token = await getToken(messaging, { vapidKey });

        if (auth.currentUser) {
          await setDoc(doc(db, "supervisor_tokens", auth.currentUser.uid), {
            token,
          });
        }
      } catch (err) {
        console.error("FCM token error:", err);
        setTokenError("Unable to get push notifications token.");
      }
    };

    requestNotificationPermission();
  }, []);

  // --------------------------
  // Breadcrumb heatmap loading
  // --------------------------


  // --------------------------
  // Which jobs get a pin. "today" keeps the map light; "filter" follows the
  // Date Range filter like before.
  // --------------------------
  const mapJobs = useMemo(() => {
    if (!mapLayers.jobs) return [];
    if (mapLayers.jobsRange !== "today") return filteredJobs;
    const { start, end } = getDateRange("TODAY");
    return filteredJobs.filter((job) => {
      const s = job.startDate?.toDate?.() || new Date(job.startDate);
      const e = job.endDate?.toDate?.() || s;
      if (!s || Number.isNaN(s.getTime?.())) return false;
      return e >= start && s <= end;
    });
  }, [filteredJobs, mapLayers.jobs, mapLayers.jobsRange]);

  // --------------------------
  // Geocoding job markers
  // --------------------------
  useEffect(() => {
    if (!isLoaded || !mapRef) {
      setJobMarkers([]);
      return;
    }
    if (!mapJobs || mapJobs.length === 0) {
      setJobMarkers([]);
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    const geocodeAddress = (address) =>
      new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            resolve(null);
          }
        });
      });

    const run = async () => {
      const newMarkers = [];
      const newCache = { ...geocodeCache };

      for (const job of mapJobs) {
        const fullAddress = [
          job.address,
          job.city,
          job.state,
          job.zip,
        ]
          .filter(Boolean)
          .join(" ");

        if (!fullAddress) continue;

        let coords = newCache[fullAddress];
        if (!coords) {
          coords = await geocodeAddress(fullAddress);
          if (coords) {
            newCache[fullAddress] = coords;
          }
        }
        if (coords) {
          newMarkers.push({
            jobId: job.id,
            lat: coords.lat,
            lng: coords.lng,
            siteName: job.siteName,
            wo: job.wo,
            scope: job.scope,
            techAssign: job.techAssign,
          });
        }
      }

      setGeocodeCache(newCache);
      setJobMarkers(newMarkers);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapJobs, isLoaded, mapRef]);

  // --------------------------
  // Derived: job check-ins
  // --------------------------
  const openVisitByUser = useMemo(() => {
    const m = {};
    openVisits.forEach((v) => {
      if (v.userId) m[v.userId] = v;
    });
    return m;
  }, [openVisits]);

  const openVisitsByJob = useMemo(() => {
    const m = {};
    openVisits.forEach((v) => {
      if (!v.jobId) return;
      (m[v.jobId] = m[v.jobId] || []).push(v);
    });
    return m;
  }, [openVisits]);

  // Same-building jobs share one pin (shows a count).
  const groupedJobMarkers = useMemo(() => {
    const groups = {};
    jobMarkers.forEach((jm) => {
      const k = coordKey(jm.lat, jm.lng);
      if (!groups[k]) groups[k] = { key: k, lat: jm.lat, lng: jm.lng, jobs: [] };
      groups[k].jobs.push(jm);
    });
    return Object.values(groups);
  }, [jobMarkers]);

  // --------------------------
  // Derived: filtered users (location & role)
  // --------------------------
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const locOk =
        locationFilter === "All Locations" ||
        (u.location && u.location === locationFilter);
      const roleOk =
        roleFilter === "All Roles" || (u.role && u.role === roleFilter);
      return locOk && roleOk;
    });
  }, [users, locationFilter, roleFilter]);

  // sort users: active first, then by last name
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aActive = !!activeLogs.find((log) => log.userId === a.id);
      const bActive = !!activeLogs.find((log) => log.userId === b.id);

      if (aActive !== bActive) return aActive ? -1 : 1;

      const aName = (a.lastName || "").toLowerCase();
      const bName = (b.lastName || "").toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [filteredUsers, activeLogs]);

  // --------------------------
  // Filter active logs for map/table based on user & filters
  // --------------------------
  const filteredActiveLogs = useMemo(() => {
    return activeLogs.filter((log) => {
      const user = users.find((u) => u.id === log.userId);
      if (!user) return false;

      // matches location & role filters
      const locOk =
        locationFilter === "All Locations" ||
        (user.location && user.location === locationFilter);
      const roleOk =
        roleFilter === "All Roles" || (user.role && user.role === roleFilter);

      if (!locOk || !roleOk) return false;

      // matches multi-select user filter
      if (
        selectedUserIds.length === 0 ||
        selectedUserIds.includes("__ALL__")
      )
        return true;

      return selectedUserIds.includes(user.id);
    });
  }, [activeLogs, users, locationFilter, roleFilter, selectedUserIds]);

  // --------------------------
  // Map center: prefer job markers → then active worker → then default
  // --------------------------
  const firstActiveWithLocation =
    filteredActiveLogs.find(
      (log) => log.liveLocation || log.clockInLocation
    ) || null;

  const mapCenter =
    jobMarkers.length > 0
      ? { lat: jobMarkers[0].lat, lng: jobMarkers[0].lng }
    
      : defaultCenter;

  const handleRecenter = () => {
    if (!mapRef) return;
    if (showWorkersOnMap && fitToWorkers()) return;
    mapRef.panTo(mapCenter);
    mapRef.setZoom(13);
  };

  // First time worker locations arrive, frame them on the map.
  useEffect(() => {
    if (didFitWorkers || !showWorkersOnMap || !mapRef) return;
    if (fitToWorkers()) setDidFitWorkers(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredActiveLogs, mapRef, showWorkersOnMap, didFitWorkers]);

  const handleMapClick = (e) => {
    if (!addingSiteMode) return;
    setNewSiteLocation({
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
    });
  };

  const saveNewSite = async () => {
    if (!newSiteName || !newSiteLocation) {
      alert("Enter a name and click a map location.");
      return;
    }
    await addDoc(collection(db, "job_sites"), {
      name: newSiteName,
      latitude: newSiteLocation.latitude,
      longitude: newSiteLocation.longitude,
      radiusMeters: Number(newSiteRadius),
    });
    setAddingSiteMode(false);
    setNewSiteLocation(null);
    setNewSiteName("");
    setNewSiteRadius(150);
    setSiteAddress("");
  };

  const deleteSite = async (id) => {
    const site = jobSites.find((s) => s.id === id);
    const siteName = site?.name || "this job site";
    const assigned = users.filter((u) => u.assignedJobSite === id);

    const names = assigned
      .map((u) => `  • ${`${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unnamed worker"}`)
      .join("\n");

    const msg = assigned.length
      ? `Are you sure you want to delete "${siteName}"?\n\n` +
        `${assigned.length} worker${assigned.length === 1 ? " is" : "s are"} still assigned to it:\n` +
        `${names}\n\n` +
        `If you continue, fencing will be removed for ${assigned.length === 1 ? "this worker" : "these workers"} ` +
        `and they will be able to clock in anywhere until you assign a new site.\n\n` +
        `This cannot be undone.`
      : `Are you sure you want to delete "${siteName}"?\n\nThis cannot be undone.`;

    if (!window.confirm(msg)) return;

    try {
      // Unassign workers FIRST so nobody is ever left pointing at a site
      // that no longer exists, even if the delete below fails.
      await Promise.all(
        assigned.map((u) =>
          updateUserGeofence(u.id, {
            assignedJobSite: null,
            geofenceEnabled: false,
          })
        )
      );
      await deleteDoc(doc(db, "job_sites", id));
    } catch (err) {
      console.error("deleteSite failed:", err);
      alert(`Could not delete "${siteName}". ${err?.message || ""}`.trim());
    }
  };

  // One click: fencing off AND site cleared, so nothing is left behind.
  const removeUserFencing = async (u) => {
    if (!u) return;
    setFenceAssignedSiteId("");
    await updateUserGeofence(u.id, {
      geofenceEnabled: false,
      assignedJobSite: null,
    });
  };

  const updateUserGeofence = async (userId, changes) => {
    const ref = doc(db, "users", userId);
    await updateDoc(ref, changes);
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, ...changes } : u))
    );
  };

  // --------------------------
  // Ping: ask a clocked-in phone for a fresh location right now.
  // The worker's app listens on its active time log and answers within a few
  // seconds if the app is open. If it's closed, the ping waits.
  // --------------------------
  const pingWorker = async (log) => {
    if (!log?.id) return;
    setPingingIds((p) => ({ ...p, [log.id]: true }));
    try {
      await updateDoc(doc(db, "time_logs", log.id), {
        pingRequestedAt: serverTimestamp(),
        pingRequestedBy: auth.currentUser?.uid || null,
      });
    } catch (err) {
      console.error("Ping failed:", err);
      alert("Could not send ping. Please try again.");
    } finally {
      setPingingIds((p) => ({ ...p, [log.id]: false }));
    }
  };

  const pingAllWorkers = async () => {
    await Promise.all(filteredActiveLogs.map((log) => pingWorker(log)));
  };

  const focusWorker = (log) => {
    const loc = log.liveLocation || log.clockInLocation;
    if (!mapRef || !isValidLatLng(loc)) return;
    mapRef.panTo({ lat: Number(loc.latitude), lng: Number(loc.longitude) });
    mapRef.setZoom(16);
    setActiveWorkerPopup(log.id);
    setActiveJobPopup(null);
  };

  const fitToWorkers = () => {
    if (!mapRef || !window.google) return false;
    const bounds = new window.google.maps.LatLngBounds();
    let count = 0;
    filteredActiveLogs.forEach((log) => {
      const loc = log.liveLocation || log.clockInLocation;
      if (isValidLatLng(loc)) {
        bounds.extend({ lat: Number(loc.latitude), lng: Number(loc.longitude) });
        count += 1;
      }
    });
    if (!count) return false;
    if (count === 1) {
      mapRef.panTo(bounds.getCenter());
      mapRef.setZoom(15);
    } else {
      mapRef.fitBounds(bounds, 60);
    }
    return true;
  };

  const updateSite = async (siteId, changes) => {
    try {
      await updateDoc(doc(db, "job_sites", siteId), changes);
    } catch (err) {
      console.error("Site update failed:", err);
      alert("Could not update the job site.");
    }
  };

  const findSiteAddress = () => {
    if (!siteAddress.trim() || !window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: siteAddress.trim() }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const loc = results[0].geometry.location;
        setNewSiteLocation({ latitude: loc.lat(), longitude: loc.lng() });
        if (!newSiteName) setNewSiteName(siteAddress.trim());
        if (mapRef) {
          mapRef.panTo({ lat: loc.lat(), lng: loc.lng() });
          mapRef.setZoom(17);
        }
      } else {
        alert("Address not found. Try adding city and state, or click the map instead.");
      }
    });
  };

  const openFencePanel = () => {
    setIsFencePanelOpen(true);
    setFenceUserId(null);
  };

  const closeFencePanel = () => {
    setIsFencePanelOpen(false);
    setFenceUserId(null);
  };

  // --------------------------
  // Fetch jobs for a user & date filter
  // --------------------------
  const fetchJobsForMap = async () => {
    const { start, end } = getDateRange(
      dateFilter,
      customStartDate,
      customEndDate
    );
  
    const snap = await getDocs(collection(db, "jobs"));
  
    let jobs = snap.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));
  
    // Filter by technician
    if (
      selectedUserIds.length > 0 &&
      !selectedUserIds.includes("__ALL__")
    ) {
      const techNames = users
        .filter(u => selectedUserIds.includes(u.id))
        .map(u => `${u.firstName} ${u.lastName}`);
  
        jobs = jobs.filter((job) => {
          const ta = job.techAssign;
        
          // normalize techAssign to array
          const techArray = Array.isArray(ta)
            ? ta
            : typeof ta === "string"
            ? [ta]
            : [];
        
          return techArray.some((t) => techNames.includes(t));
        });
        
    }
  
    // Filter by date overlap
    jobs = jobs.filter(job => {
      const s = job.startDate?.toDate?.() || new Date(job.startDate);
      const e = job.endDate?.toDate?.() || s;
  
      if (!s) return false;
  
      // 🔑 overlap logic
      return (
        (!start || e >= start) &&
        (!end || s <= end)
      );
    });
  
    setFilteredJobs(jobs);
  };

  const handleUserRowClick = (user) => {
    if (!user) return;
  
    if (expandedUserId === user.id) {
      setExpandedUserId(null);
      setSelectedUserForJobs(null);
      return;
    }
  
    setExpandedUserId(user.id);
    setSelectedUserForJobs(user);
  };
  

  return (
    <div style={{ padding: "1rem", position: "relative", overflow: "hidden" }}>
      <h1>Supervisor Dashboard</h1>

      {tokenError && (
        <div
          style={{
            marginBottom: 8,
            padding: 8,
            background: "#ffe0e0",
            borderRadius: 4,
            color: "#b00020",
          }}
        >
          {tokenError}
        </div>
      )}

      {/* Top controls: worker multi-select & site tools */}
      <div
        style={{
          display: "flex",
          gap: "1rem",
          alignItems: "flex-start",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Filter Workers</div>
          <select
            multiple
            value={selectedUserIds}
            onChange={(e) => {
              const options = Array.from(e.target.options);
              const values = options
                .filter((o) => o.selected)
                .map((o) => o.value);
              setSelectedUserIds(values);
            }}
            style={{ minWidth: 220, minHeight: 110 }}
          >
            <option value="__ALL__">Show All</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={handleRecenter}>Recenter Map</button>
          <button onClick={() => setAddingSiteMode((v) => !v)}>
            {addingSiteMode ? "Cancel New Job Site" : "Add New Job Site"}
          </button>
          <button onClick={openFencePanel}>Set Fencing</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={() => setShowWorkersOnMap((v) => !v)}>
            {showWorkersOnMap ? "Hide Worker Locations" : "Show Worker Locations"}
          </button>
          <button
            onClick={pingAllWorkers}
            disabled={!showWorkersOnMap || filteredActiveLogs.length === 0}
            title="Ask every clocked-in phone for its location right now"
          >
            Ping All Clocked-In ({filteredActiveLogs.length})
          </button>
        </div>


        {addingSiteMode && (
          <div
            style={{
              padding: "0.5rem 1rem",
              border: "1px solid #ccc",
              borderRadius: 6,
              maxWidth: 260,
            }}
          >
            <div style={{ fontWeight: 600 }}>New Job Site</div>
            <input
              placeholder="Job Site Name"
              value={newSiteName}
              onChange={(e) => setNewSiteName(e.target.value)}
              style={{ display: "block", marginBottom: 6, width: "100%" }}
            />
            <input
              type="number"
              placeholder="Radius (meters)"
              value={newSiteRadius}
              onChange={(e) => setNewSiteRadius(e.target.value)}
              style={{ display: "block", marginBottom: 6, width: "100%" }}
            />
            <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
              <input
                placeholder="Address (optional)"
                value={siteAddress}
                onChange={(e) => setSiteAddress(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && findSiteAddress()}
                style={{ flex: 1, minWidth: 0 }}
              />
              <button onClick={findSiteAddress}>Find</button>
            </div>
            <div style={{ fontSize: 12, marginBottom: 4 }}>
              Find an address, or click on the map to place the site center.
            </div>
            <button onClick={saveNewSite}>Save Job Site</button>
            {newSiteLocation && (
              <div style={{ marginTop: 4, fontSize: 12 }}>
                Location: {newSiteLocation.latitude.toFixed(5)},{" "}
                {newSiteLocation.longitude.toFixed(5)}
              </div>
            )}
          </div>
        )}

        {/* Global filters: Location / Role / Date */}
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: 6,
            padding: "0.75rem",
            minWidth: 260,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Filters</div>

          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>
              Location:
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                style={{ display: "block", width: "100%", marginTop: 2 }}
              >
                {LOCATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div style={{ marginBottom: 6 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>
              Role:
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{ display: "block", width: "100%", marginTop: 2 }}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div style={{ marginBottom: 4 }}>
            <label style={{ fontSize: 13, fontWeight: 500 }}>
              Date Range:
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                style={{ display: "block", width: "100%", marginTop: 2 }}
              >
                {DATE_FILTER_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.replace("_", " ")}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {dateFilter === "CUSTOM" && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <div style={{ marginBottom: 4 }}>
                <label>
                  Start Date:
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{ marginLeft: 4 }}
                  />
                </label>
              </div>
              <div>
                <label>
                  End Date:
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    style={{ marginLeft: 4 }}
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Map */}
      <h2>Real-Time Worker Locations & Job Sites</h2>

      {/* Map layers: turn pins on/off so worker dots are easy to see */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "6px 16px",
          padding: "8px 10px",
          marginBottom: 6,
          border: "1px solid #cfd8e3",
          borderRadius: 6,
          background: "#f7f9fc",
          fontSize: 13,
        }}
      >
        <strong>Show on map:</strong>
        <label>
          <input
            type="checkbox"
            checked={showWorkersOnMap}
            onChange={(e) => setShowWorkersOnMap(e.target.checked)}
          />{" "}
          <span style={{ color: "#2563eb", fontWeight: 700 }}>●</span> Workers
        </label>
        <label>
          <input
            type="checkbox"
            checked={showClockInPins}
            onChange={(e) => setShowClockInPins(e.target.checked)}
          />{" "}
          ▼ Where they clocked in
        </label>
        <label>
          <input
            type="checkbox"
            checked={mapLayers.jobSites}
            onChange={(e) => setLayer("jobSites", e.target.checked)}
          />{" "}
          ◯ Job sites (fences)
        </label>
        <label>
          <input
            type="checkbox"
            checked={mapLayers.jobs}
            onChange={(e) => setLayer("jobs", e.target.checked)}
          />{" "}
          <span style={{ color: "#64748b" }}>⌂</span> Jobs
        </label>
        {mapLayers.jobs && (
          <select
            value={mapLayers.jobsRange}
            onChange={(e) => setLayer("jobsRange", e.target.value)}
            style={{ fontSize: 13 }}
            title="Which scheduled jobs get a pin"
          >
            <option value="today">Today only</option>
            <option value="filter">Match Date Range filter</option>
          </select>
        )}
        <label>
          <input
            type="checkbox"
            checked={mapLayers.labels}
            onChange={(e) => setLayer("labels", e.target.checked)}
          />{" "}
          Names on job pins
        </label>
        <span style={{ color: "#555" }}>
          <span style={{ color: "#16a34a" }}>⌂</span> green = someone checked in ·
          number = jobs at same address · hover a pin for its name
        </span>
      </div>

      {isLoaded && (
        <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={11}
        onClick={(e) => {
          handleMapClick(e);
          setActiveJobPopup(null);   // ✅ CLOSE POPUP ON MAP CLICK
          setActiveWorkerPopup(null);
          setActiveSitePopup(null);
        }}
        onLoad={(map) => setMapRef(map)}
      >
      
          {/* Active workers (pins + routes) */}
          {showWorkersOnMap &&
  filteredActiveLogs.map((log) => {
    const loc = log.liveLocation || log.clockInLocation;
    if (!isValidLatLng(loc)) return null;

    const fill = getUserColorHex(log.userId);

    return (
      <Marker
        key={log.id}
        position={{
          lat: Number(loc.latitude),
          lng: Number(loc.longitude),
        }}
        icon={{
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: fill,
          fillOpacity: 0.95,
          strokeColor: "#ffffff",
          strokeOpacity: 1,
          strokeWeight: 2,
          scale: 8, // bigger = more “dot”
        }}
        title={log.userName || "Worker"}
        zIndex={1000}
        label={{
          text: (log.userName?.split(" ")[0] || "Worker"),
          fontSize: "12px",
          fontWeight: "600",
        }}
        onClick={() => {
          setActiveWorkerPopup(log.id);
          setActiveJobPopup(null);
        }}
      />
    );
  })}

          {/* Where each active worker clocked in */}
          {showWorkersOnMap &&
            showClockInPins &&
            filteredActiveLogs.map((log) => {
              const inLoc = log.clockInLocation;
              if (!isValidLatLng(inLoc)) return null;
              return (
                <Marker
                  key={`in-${log.id}`}
                  position={{ lat: Number(inLoc.latitude), lng: Number(inLoc.longitude) }}
                  icon={{
                    path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
                    fillColor: getUserColorHex(log.userId),
                    fillOpacity: 0.6,
                    strokeColor: "#111827",
                    strokeWeight: 1,
                    scale: 5,
                  }}
                  title={`${log.userName || "Worker"} clocked in here`}
                  zIndex={500}
                  onClick={() => {
                    setActiveWorkerPopup(log.id);
                    setActiveJobPopup(null);
                  }}
                />
              );
            })}

          {/* Today's path for the selected worker */}
          {showWorkersOnMap &&
            activeWorkerPopup &&
            (() => {
              const log = filteredActiveLogs.find((l) => l.id === activeWorkerPopup);
              const path = (log?.route || [])
                .filter(isValidLatLng)
                .map((p) => ({ lat: Number(p.latitude), lng: Number(p.longitude) }));
              if (path.length < 2) return null;
              return (
                <Polyline
                  path={path}
                  options={{
                    strokeColor: getUserColorHex(log.userId),
                    strokeOpacity: 0.8,
                    strokeWeight: 3,
                  }}
                />
              );
            })()}

          {showWorkersOnMap &&
            activeWorkerPopup &&
            (() => {
              const log = filteredActiveLogs.find((l) => l.id === activeWorkerPopup);
              const loc = log && (log.liveLocation || log.clockInLocation);
              if (!log || !isValidLatLng(loc)) return null;
              const user = users.find((u) => u.id === log.userId);
              const site = jobSites.find((s) => s.id === user?.assignedJobSite);
              const geo = site ? getWorkerGeofenceStatus(loc, site) : null;
              const lastDate = toDateSafe(log.lastUpdatedLive) || toDateSafe(log.clockIn);
              const stale = lastDate && (Date.now() - lastDate.getTime()) / 60000 > STALE_MINUTES;
              const inGeo = log.clockInGeofence;
              const ping = getPingState(log);
              return (
                <InfoWindow
                  position={{ lat: Number(loc.latitude), lng: Number(loc.longitude) }}
                  onCloseClick={() => setActiveWorkerPopup(null)}
                >
                  <div style={{ minWidth: 220, fontSize: 13, lineHeight: 1.45 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>
                      {log.userName || "Worker"}
                      {log.isLunch ? " (lunch)" : ""}
                    </div>
                    <div>
                      <strong>Clocked in:</strong>{" "}
                      {toDateSafe(log.clockIn)?.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) || "N/A"}
                      {inGeo?.siteName
                        ? ` · ${inGeo.inside ? "on site" : `${inGeo.distanceMeters}m from`} ${inGeo.siteName}`
                        : ""}
                    </div>
                    <div style={{ color: stale ? "#b91c1c" : undefined }}>
                      <strong>Last location:</strong> {timeAgo(lastDate)}
                      {stale ? " (app closed or location off)" : ""}
                    </div>
                    {loc.accuracy != null && (
                      <div><strong>Accuracy:</strong> ±{Math.round(loc.accuracy)}m</div>
                    )}
                    {site && geo && (
                      <div style={{ color: geo.inside ? "#0F9D58" : "#DB4437", fontWeight: 600 }}>
                        {geo.inside ? "Inside" : "Outside"} {site.name} ({geo.dist}m)
                      </div>
                    )}
                    {openVisitByUser[log.userId] ? (
                      <div style={{ color: "#166534", fontWeight: 600 }}>
                        At job: {openVisitByUser[log.userId].siteName || "Job"}
                        {openVisitByUser[log.userId].wo ? ` (WO #${openVisitByUser[log.userId].wo})` : ""}
                        {" · "}
                        {formatMinutes(visitMinutes(openVisitByUser[log.userId]))}
                      </div>
                    ) : (
                      <div style={{ color: "#6b7280" }}>Not checked in to a job</div>
                    )}
                    {ping && <div style={{ color: ping.color }}>{ping.text}</div>}
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button onClick={() => pingWorker(log)} disabled={pingingIds[log.id]}>
                        {pingingIds[log.id] ? "Pinging…" : "Ping"}
                      </button>
                      {mapsLink(loc) && (
                        <a href={mapsLink(loc)} target="_blank" rel="noreferrer">
                          Open in Maps
                        </a>
                      )}
                    </div>
                  </div>
                </InfoWindow>
              );
            })()}


          {/* New job site preview */}
          {newSiteLocation && addingSiteMode && (
            <>
              <Marker
                position={{
                  lat: newSiteLocation.latitude,
                  lng: newSiteLocation.longitude,
                }}
                icon={{
                  url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                }}
              />
              <Circle
                center={{
                  lat: newSiteLocation.latitude,
                  lng: newSiteLocation.longitude,
                }}
                radius={Number(newSiteRadius)}
                options={{
                  strokeColor: "#4285F4",
                  strokeOpacity: 0.9,
                  strokeWeight: 2,
                  fillColor: "#4285F4",
                  fillOpacity: 0.2,
                }}
              />
            </>
          )}
{activeJobPopup && (
  <InfoWindow
    position={{ lat: activeJobPopup.lat, lng: activeJobPopup.lng }}
    onCloseClick={() => setActiveJobPopup(null)}
    options={{ disableAutoPan: true }}   // ✅ ADD THIS LINE
  >
    <div style={{ minWidth: 200, maxWidth: 280, maxHeight: 260, overflowY: "auto", fontSize: 13 }}>
      {(activeJobPopup.jobs || [activeJobPopup]).map((jm, i) => {
        const here = openVisitsByJob[jm.jobId] || [];
        return (
          <div
            key={jm.jobId || i}
            style={{ borderTop: i ? "1px solid #e5e7eb" : "none", paddingTop: i ? 6 : 0, marginTop: i ? 6 : 0 }}
          >
            <div style={{ fontWeight: 700 }}>{jm.siteName || "Job Site"}</div>
            <div><strong>WO:</strong> {jm.wo || "N/A"}</div>
            <div>
              <strong>Scope:</strong>{" "}
              {jm.scope ? (String(jm.scope).length > 120 ? `${String(jm.scope).slice(0, 120)}…` : jm.scope) : "N/A"}
            </div>
            {here.map((v) => (
              <div key={v.id} style={{ color: "#166534", fontWeight: 600 }}>
                ● {v.userName || "Worker"} checked in · {formatMinutes(visitMinutes(v))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  </InfoWindow>
)}

{activeSitePopup && (
  <InfoWindow
    position={{ lat: Number(activeSitePopup.latitude), lng: Number(activeSitePopup.longitude) }}
    onCloseClick={() => setActiveSitePopup(null)}
    options={{ disableAutoPan: true }}
  >
    <div style={{ minWidth: 180, fontSize: 13 }}>
      <div style={{ fontWeight: 700 }}>{activeSitePopup.name || "Job Site"}</div>
      <div>Fence radius: {activeSitePopup.radiusMeters} m</div>
      <div>
        Workers assigned:{" "}
        {users.filter((u) => u.assignedJobSite === activeSitePopup.id).length}
      </div>
    </div>
  </InfoWindow>
)}


          {/* Existing job sites with geofence color based on first worker assigned */}
          {mapLayers.jobSites && jobSites.map((site) => {
            if (!isValidLatLng(site)) return null;

            // find workers assigned to this job site
            const workersAtSite = filteredActiveLogs.filter(
              (log) =>
                users.find((u) => u.id === log.userId)?.assignedJobSite ===
                site.id
            );

            let status = null;
            if (workersAtSite.length > 0) {
              const log = workersAtSite[0];
              const loc = log.liveLocation || log.clockInLocation;
              if (isValidLatLng(loc)) {
                status = getWorkerGeofenceStatus(loc, site);
              }
            }

            const strokeColor = status?.inside ? "#00C851" : "#ff4444";
            const fillColor = status?.inside ? "#00C851" : "#ff4444";

            return (
              <React.Fragment key={site.id}>
                <Marker
                  position={{
                    lat: Number(site.latitude),
                    lng: Number(site.longitude),
                  }}
                  icon={{
                    // small center dot; the circle already shows the site
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: "#4285F4",
                    fillOpacity: 0.9,
                    strokeColor: "#ffffff",
                    strokeWeight: 1,
                    scale: 4,
                  }}
                  zIndex={50}
                  title={site.name}
                  label={
                    mapLayers.labels
                      ? { text: site.name || "Site", fontSize: "11px", fontWeight: "600" }
                      : undefined
                  }
                  onClick={() => {
                    setActiveSitePopup(site);
                    setActiveJobPopup(null);
                  }}
                />
                <Circle
                  onClick={(e) => {
                    // While adding a new site, a click inside an existing
                    // circle still places the new site.
                    if (addingSiteMode) {
                      handleMapClick(e);
                      return;
                    }
                    setActiveSitePopup(site);
                    setActiveJobPopup(null);
                  }}
                  center={{
                    lat: Number(site.latitude),
                    lng: Number(site.longitude),
                  }}
                  radius={Number(site.radiusMeters)}
                  options={{
                    strokeColor,
                    strokeOpacity: 0.9,
                    strokeWeight: 2,
                    fillColor,
                    fillOpacity: 0.2,
                  }}
                />
              </React.Fragment>
            );
          })}

       {/* JOB MARKERS (for selected user & date filters) */}
{mapLayers.jobs && groupedJobMarkers.map((g) => {
  const count = g.jobs.length;
  const first = g.jobs[0];
  const live = g.jobs.some((j) => (openVisitsByJob[j.jobId] || []).length > 0);
  const names = g.jobs.map((j) => j.siteName || (j.wo ? `WO #${j.wo}` : "Job")).join("\n");

  let label;
  if (count > 1) {
    label = { text: String(count), fontSize: "10px", fontWeight: "700", color: "#ffffff" };
  } else if (mapLayers.labels) {
    label = { text: first.siteName || first.wo || "Job", fontSize: "11px", fontWeight: "600" };
  }

  return (
    <Marker
      key={g.key}
      position={{ lat: g.lat, lng: g.lng }}
      zIndex={live ? 400 : 100}
      title={names}
      icon={{
        path: HOUSE_PATH, // small house: gray = scheduled, green = someone checked in
        fillColor: live ? "#16a34a" : "#64748b",
        fillOpacity: 0.9,
        strokeColor: "#ffffff",
        strokeWeight: 1,
        scale: count > 1 ? 1.3 : 1.05,
        anchor: new window.google.maps.Point(12, 12),
        labelOrigin: new window.google.maps.Point(12, count > 1 ? 15 : -4),
      }}
      label={label}
      onClick={() => {
        handleJobMarkerClick(g);
        setActiveSitePopup(null);
      }}
    />
  );
})}


          {/* HEATMAP OF WORKER MOVEMENT */}
         

        </GoogleMap>
      )}

      {/* ACTIVE WORKERS TABLE */}
      <h3 style={{ marginTop: 16 }}>Active Workers (Clocked-In)</h3>
      <table
        style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}
      >
        <thead>
          <tr style={{ background: "#f3f3f3" }}>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Name</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Checked In At Job</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Job Site</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Distance</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>ETA</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Speed</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Status</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>
              Last Update
            </th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Idle (min)</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Clocked In</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Ping</th>
          </tr>
        </thead>
        <tbody>
          {filteredActiveLogs.map((log) => {
            const loc = log.liveLocation || log.clockInLocation;
            if (!isValidLatLng(loc)) return null;

            const user = users.find((u) => u.id === log.userId);
            const site = jobSites.find(
              (s) => s.id === user?.assignedJobSite
            );

            const geoStatus = site
              ? getWorkerGeofenceStatus(loc, site)
              : null;
            const miles = geoStatus ? metersToMiles(geoStatus.dist) : null;
            const formattedMiles = miles != null ? formatMiles(miles) : "N/A";
            const eta =
              miles != null ? getEtaMinutesFromMiles(miles) + " min" : "N/A";

            const speedMph = getSpeedMphFromRoute(log.route || []);
            const status = getWorkerStatus(geoStatus?.inside, speedMph, !!site);

            const lastUpdatedDate =
              toDateSafe(log.lastUpdatedLive) ||
              toDateSafe(log.clockIn) ||
              null;
            const isStale =
              lastUpdatedDate &&
              (Date.now() - lastUpdatedDate.getTime()) / 60000 > STALE_MINUTES;
            const lastUpdatedStr = lastUpdatedDate
              ? `${lastUpdatedDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} (${timeAgo(lastUpdatedDate)})`
              : "N/A";
            const clockInDate = toDateSafe(log.clockIn);
            const inGeo = log.clockInGeofence;
            const ping = getPingState(log);

            let idleMinutes = null;
            if (Array.isArray(log.route) && log.route.length > 0) {
              const lastPoint = log.route[log.route.length - 1];
              if (lastPoint.timestamp) {
                idleMinutes = Math.floor(
                  (Date.now() - lastPoint.timestamp) / 60000
                );
              }
            }

            return (
              <tr key={log.id}>
                <td style={{ padding: 8, border: "1px solid #ccc" }}>
                  <button
                    onClick={() => focusWorker(log)}
                    style={{ background: "none", border: "none", padding: 0, color: "#2563eb", cursor: "pointer", fontWeight: 600 }}
                    title="Show on map"
                  >
                    {log.userName || "Worker"}
                  </button>
                  {log.isLunch ? " (lunch)" : ""}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc", fontSize: 13 }}>
                  {openVisitByUser[log.userId] ? (
                    (() => {
                      const v = openVisitByUser[log.userId];
                      const inAt = toDateSafe(v.checkInAt);
                      const far = v.checkInDistanceMeters != null && v.checkInDistanceMeters > FAR_FROM_JOB_METERS;
                      return (
                        <>
                          <div style={{ fontWeight: 600, color: "#166534" }}>
                            {v.siteName || "Job"}
                            {v.wo ? ` (WO #${v.wo})` : ""}
                          </div>
                          <div>
                            since{" "}
                            {inAt
                              ? inAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                              : "—"}{" "}
                            · <strong>{formatMinutes(visitMinutes(v))}</strong>
                          </div>
                          {v.checkInDistanceMeters != null && (
                            <div style={{ color: far ? "#b45309" : "#6b7280", fontWeight: far ? 600 : 400 }}>
                              checked in {v.checkInDistanceMeters < 1000
                                ? `${v.checkInDistanceMeters} m`
                                : `${(v.checkInDistanceMeters / 1609.34).toFixed(1)} mi`} from job
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    <span style={{ color: "#6b7280" }}>Not checked in</span>
                  )}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc" }}>
                  {site ? site.name : "No Assigned Site"}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc" }}>
                  {formattedMiles}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc" }}>{eta}</td>
                <td style={{ padding: 8, border: "1px solid #ccc" }}>
                  {speedMph.toFixed(1)} mph
                </td>
                <td
                  style={{
                    padding: 8,
                    border: "1px solid #ccc",
                    color: status.color,
                    fontWeight: 600,
                  }}
                >
                  {status.text}
                </td>
                <td
                  style={{
                    padding: 8,
                    border: "1px solid #ccc",
                    color: isStale ? "#b91c1c" : undefined,
                    fontWeight: isStale ? 600 : undefined,
                  }}
                  title={isStale ? "No update in over 10 minutes: app closed, phone asleep, or location off" : ""}
                >
                  {lastUpdatedStr}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc" }}>
                  {idleMinutes != null ? idleMinutes : "N/A"}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc", fontSize: 13 }}>
                  {clockInDate
                    ? clockInDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                    : "N/A"}
                  {inGeo?.siteName && (
                    <div style={{ color: inGeo.inside ? "#0F9D58" : "#DB4437" }}>
                      {inGeo.inside ? "On site" : `${inGeo.distanceMeters}m away`}
                    </div>
                  )}
                  {mapsLink(log.clockInLocation) && (
                    <div>
                      <a href={mapsLink(log.clockInLocation)} target="_blank" rel="noreferrer">
                        Where
                      </a>
                    </div>
                  )}
                </td>
                <td style={{ padding: 8, border: "1px solid #ccc", fontSize: 13 }}>
                  <button onClick={() => pingWorker(log)} disabled={pingingIds[log.id]}>
                    {pingingIds[log.id] ? "Pinging…" : "Ping"}
                  </button>
                  {ping && <div style={{ color: ping.color, marginTop: 2 }}>{ping.text}</div>}
                </td>
              </tr>
            );
          })}
          {filteredActiveLogs.length === 0 && (
            <tr>
              <td
                colSpan={11}
                style={{
                  textAlign: "center",
                  padding: 10,
                  border: "1px solid #ccc",
                }}
              >
                {showWorkersOnMap
                  ? "No active workers right now."
                  : "Worker locations are hidden. Click \"Show Worker Locations\"."}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ALL WORKERS TABLE */}
      <h3 style={{ marginTop: 24 }}>All Workers (Clocked-In First)</h3>
      <SupervisorHowTo />
      <table
        style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}
      >
        <thead>
          <tr style={{ background: "#f3f3f3" }}>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Name</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Location</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Role</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Job Site</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Distance</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>ETA</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>Status</th>
            <th style={{ padding: 8, border: "1px solid #ccc" }}>
              Last Known Update
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedUsers.map((u) => {
            const activeLog = activeLogs.find((log) => log.userId === u.id);
            const lastLog = activeLog || lastLogByUser[u.id];
            
            const techName = `${u.firstName} ${u.lastName}`;
            const expandedJobs = filteredJobs.filter((job) => {
              const ta = job.techAssign;
              const techArray = Array.isArray(ta)
                ? ta
                : typeof ta === "string"
                ? [ta]
                : [];
            
              return techArray.includes(techName);
            });
            

            const site = jobSites.find((s) => s.id === u.assignedJobSite);

            let loc = null;
            let lastUpdateDate = null;

            if (activeLog) {
              loc = activeLog.liveLocation || activeLog.clockInLocation;
              lastUpdateDate =
                activeLog.lastUpdatedLive?.toDate?.() ||
                activeLog.clockIn?.toDate?.() ||
                null;
            } else if (lastLog) {
              loc = lastLog.clockOutLocation || lastLog.clockInLocation || null;
              lastUpdateDate =
                lastLog.clockOut?.toDate?.() ||
                lastLog.clockIn?.toDate?.() ||
                null;
            }

            const geoStatus =
              loc && site ? getWorkerGeofenceStatus(loc, site) : null;
            const miles =
              geoStatus && geoStatus.dist != null
                ? metersToMiles(geoStatus.dist)
                : null;
            const formattedMiles =
              miles != null ? formatMiles(miles) : "N/A";
            const eta =
              miles != null ? getEtaMinutesFromMiles(miles) + " min" : "N/A";

            const speedMph =
              activeLog && activeLog.route
                ? getSpeedMphFromRoute(activeLog.route)
                : 0;

            let status;
            if (activeLog) {
              status = getWorkerStatus(geoStatus?.inside, speedMph, !!site);
            } else {
              status = { text: "Not Clocked In", color: "#666666" };
            }

            const lastUpdateStr = lastUpdateDate
              ? lastUpdateDate.toLocaleString()
              : "N/A";

            const isExpanded = expandedUserId === u.id;

            

            return (
              <React.Fragment key={u.id}>
                <tr
                  onClick={() => handleUserRowClick(u)}
                  style={{
                    cursor: "pointer",
                    background: isExpanded ? "#f9fbff" : "transparent",
                  }}
                >
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {u.firstName} {u.lastName}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {u.location || "—"}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {u.role || "—"}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {site ? site.name : "No Assigned Site"}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {formattedMiles}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {eta}
                  </td>
                  <td
                    style={{
                      padding: 8,
                      border: "1px solid #ccc",
                      color: status.color,
                      fontWeight: 600,
                    }}
                  >
                    {status.text}
                  </td>
                  <td style={{ padding: 8, border: "1px solid #ccc" }}>
                    {lastUpdateStr}
                  </td>
                </tr>

             {/* Expanded jobs row */}
             {isExpanded && (
  <tr>
    <td
      colSpan={8}
      style={{
        padding: 10,
        border: "1px solid #ccc",
        background: "#fafafa",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        Jobs for {u.firstName} {u.lastName}
      </div>

      {expandedJobs.length === 0 && (
        <div style={{ fontSize: 13 }}>
          No jobs found for this user in the selected date range.
        </div>
      )}

      {expandedJobs.map((job) => {
        const startDate = job.startDate?.toDate?.();
        const endDate = job.endDate?.toDate?.();
        const jobVisits = rangeVisits.filter(
          (v) => v.jobId === job.id && v.userId === u.id
        );
        const jobMins = jobVisits.reduce((sum, v) => sum + visitMinutes(v), 0);

        return (
          <div
            key={job.id}
            style={{
              padding: "6px 8px",
              borderRadius: 4,
              border: "1px solid #ddd",
              marginBottom: 6,
              background: "#ffffff",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                fontSize: 13,
              }}
            >
              <span><strong>WO:</strong> {job.wo || "N/A"}</span>
              <span><strong>Site:</strong> {job.siteName || "N/A"}</span>
              <span><strong>Status:</strong> {job.status || "N/A"}</span>
              <span><strong>Start:</strong> {startDate ? startDate.toLocaleDateString() : "N/A"}</span>
              <span><strong>End:</strong> {endDate ? endDate.toLocaleDateString() : "N/A"}</span>
            </div>
            <div style={{ fontSize: 12, marginTop: 4, color: jobVisits.length ? "#166534" : "#6b7280" }}>
              {jobVisits.length
                ? `Checked in ${jobVisits.length}× · ${formatMinutes(jobMins)} on this job` +
                  (jobVisits.some((v) => v.status === "checked_in") ? " · on site now" : "")
                : "No check-in recorded"}
            </div>
          </div>
        );
      })}
    </td>
  </tr>
)}
        
          </React.Fragment>
        );
        
      })} 

              
        </tbody> 
         
      </table>
       

      {/* JOB CHECK-IN LOG (job_visits in the Date Range filter) */}
      {(() => {
        const visibleIds = new Set(filteredUsers.map((u) => u.id));
        const pickedIds =
          selectedUserIds.length > 0 && !selectedUserIds.includes("__ALL__")
            ? new Set(selectedUserIds)
            : null;
        const logVisits = rangeVisits.filter(
          (v) => visibleIds.has(v.userId) && (!pickedIds || pickedIds.has(v.userId))
        );
        const totalMins = logVisits.reduce((sum, v) => sum + visitMinutes(v), 0);
        return (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ marginBottom: 4 }}>
              <button
                type="button"
                onClick={() => setShowVisitLog((v) => !v)}
                style={{ background: "none", border: "none", padding: 0, font: "inherit", cursor: "pointer" }}
              >
                {showVisitLog ? "▾" : "▸"} Job Check-In Log ({logVisits.length} · {formatMinutes(totalMins)})
              </button>
            </h3>
            <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
              Every job check-in / check-out in the Date Range, using the Worker, Location and Role filters above.
            </div>
            {showVisitLog && <VisitList visits={logVisits} showJob />}
          </div>
        );
      })()}

      {/* Job site list */}
      <h3 style={{ marginTop: 24 }}>Existing Job Sites</h3>
      <ul>
        {jobSites.map((site) => {
          const assignedCount = users.filter((u) => u.assignedJobSite === site.id).length;
          return (
            <li key={site.id} style={{ marginBottom: 6 }}>
              <strong>{site.name}</strong> — radius{" "}
              <input
                type="number"
                min={10}
                defaultValue={site.radiusMeters}
                key={`${site.id}-${site.radiusMeters}`}
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v > 0 && v !== Number(site.radiusMeters)) {
                    updateSite(site.id, { radiusMeters: v });
                  }
                }}
                style={{ width: 70 }}
              />
              m · {assignedCount} worker{assignedCount === 1 ? "" : "s"}{" "}
              <button
                onClick={() => {
                  if (!mapRef) return;
                  mapRef.panTo({ lat: Number(site.latitude), lng: Number(site.longitude) });
                  mapRef.setZoom(16);
                }}
              >
                View
              </button>{" "}
              <button onClick={() => deleteSite(site.id)}>Delete</button>
            </li>
          );
        })}
      </ul>

      {/* Slide-out fencing panel */}
      {isFencePanelOpen && createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: "360px",
            background: "#fff",
            boxShadow: "-4px 0 12px rgba(0,0,0,0.15)",
            padding: "1rem",
            zIndex: 999999,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            maxWidth: "100vw",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "0.5rem",
              alignItems: "center",
            }}
          >
            <h2 style={{ margin: 0 }}>Set Fencing</h2>
            <button onClick={closeFencePanel}>Close</button>
          </div>

          <div style={{ fontSize: 12, color: "#555", marginBottom: 6 }}>
            Pick a worker. Geofence ON = they can only clock in inside their
            assigned site. OFF = they can clock in anywhere (their location is
            still recorded).
          </div>
          <input
            placeholder="Search workers…"
            value={fenceSearch}
            onChange={(e) => setFenceSearch(e.target.value)}
            style={{ marginBottom: 8, padding: 6 }}
          />

          {/* User list in fencing panel */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              marginBottom: "0.75rem",
            }}
          >
            {[...users]
              .filter((u) =>
                `${u.firstName || ""} ${u.lastName || ""}`
                  .toLowerCase()
                  .includes(fenceSearch.trim().toLowerCase())
              )
              .sort((a, b) =>
                `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(
                  `${b.firstName || ""} ${b.lastName || ""}`
                )
              )
              .map((u) => {
              const assignedSite = jobSites.find(
                (s) => s.id === u.assignedJobSite
              );
              return (
                <div
                  key={u.id}
                  onClick={() => setFenceUserId(u.id)}
                  style={{
                    padding: "0.4rem 0.5rem",
                    marginBottom: "0.25rem",
                    borderRadius: 4,
                    cursor: "pointer",
                    background:
                      fenceUserId === u.id
                        ? "rgba(66,133,244,0.12)"
                        : "transparent",
                    border:
                      fenceUserId === u.id
                        ? "1px solid #4285F4"
                        : "1px solid #eee",
                  }}
                
                >
                  <div style={{ fontWeight: 600 }}>
                    {u.firstName} {u.lastName}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    Geofence:{" "}
                    {u.geofenceEnabled && assignedSite ? (
                      <span style={{ color: "#0F9D58" }}>On</span>
                    ) : u.geofenceEnabled ? (
                      <span style={{ color: "#b45309" }}>On, but no site (anywhere allowed)</span>
                    ) : (
                      <span style={{ color: "#DB4437" }}>Off</span>
                    )}
                    {" • "}
                    Site:{" "}
                    {assignedSite ? assignedSite.name : "None"}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Selected user settings */}
          {fenceUser && (
            <div
              style={{
                borderTop: "1px solid #ddd",
                paddingTop: "0.75rem",
                marginTop: "0.25rem",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Settings for {fenceUser.firstName} {fenceUser.lastName}
              </div>

              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={fenceUser.geofenceEnabled || false}
                    onChange={(e) =>
                      updateUserGeofence(fenceUser.id, {
                        geofenceEnabled: e.target.checked,
                      })
                    }
                  />{" "}
                  Require Geofence for Clock-In
                </label>
              </div>

              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 13, marginBottom: 2 }}>
                  Assigned Job Site
                </div>
                <select
                  value={fenceAssignedSiteId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFenceAssignedSiteId(val);
                    updateUserGeofence(fenceUser.id, {
                      assignedJobSite: val || null,
                    });
                  }}
                  style={{ width: "100%" }}
                >
                  <option value="">None</option>
                  {jobSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                  {fenceUser.geofenceEnabled
                    ? fenceAssignedSiteId
                      ? "Clock-in is blocked outside this site."
                      : "Pick a site — with no site they can still clock in anywhere."
                    : fenceAssignedSiteId
                    ? "Not enforced. The site is only used to show on-site / distance."
                    : "Clock-in allowed anywhere."}
                </div>
                {jobSites.length === 0 && (
                  <div style={{ fontSize: 12, color: "#b45309", marginTop: 4 }}>
                    No job sites yet. Close this panel and use "Add New Job Site".
                  </div>
                )}
              </div>

              {(fenceUser.geofenceEnabled || fenceAssignedSiteId) && (
                <button
                  type="button"
                  onClick={() => removeUserFencing(fenceUser)}
                  title="Turns fencing off and clears the assigned site"
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    border: "1px solid #DB4437",
                    background: "#fff",
                    color: "#DB4437",
                    borderRadius: 4,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Remove Fencing (clock in anywhere)
                </button>
              )}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default SupervisorDashboard;
